import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Monitor, BookOpen, Zap, MapPin, Star, AlertCircle, ChevronDown } from 'lucide-react';

export const BlogCategoryFilter = ({ selectedCategory, onCategoryChange }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const categories = [
    {
      id: 'guides',
      label: 'Guides & Basics',
      icon: BookOpen,
      color: 'from-blue-500 to-blue-600',
      subcategories: [
        { id: 'IPTV Guides', label: 'IPTV Guides' },
        { id: 'IPTV Basics', label: 'IPTV Basics' },
        { id: 'Setup Guides', label: 'Setup Guides' }
      ]
    },
    {
      id: 'devices',
      label: 'Devices & Setup',
      icon: Monitor,
      color: 'from-purple-500 to-purple-600',
      subcategories: [
        { id: 'Device Setup', label: 'Device Setup' },
        { id: 'Offers', label: 'Free Trial & Offers' }
      ]
    },
    {
      id: 'subscriptions',
      label: 'Plans & Pricing',
      icon: Zap,
      color: 'from-green-500 to-green-600',
      subcategories: [
        { id: 'Subscriptions', label: 'Subscriptions' },
        { id: 'Comparisons', label: 'Price Comparisons' }
      ]
    },
    {
      id: 'regions',
      label: 'Regions',
      icon: MapPin,
      color: 'from-orange-500 to-orange-600',
      subcategories: [
        { id: 'Regional', label: 'Regional' },
        { id: 'Regional IPTV', label: 'Regional IPTV' }
      ]
    },
    {
      id: 'reviews',
      label: 'Reviews',
      icon: Star,
      color: 'from-green-500 to-green-600',
      subcategories: [
        { id: 'IPTV Reviews', label: 'IPTV Reviews' },
        { id: 'App Reviews', label: 'App Reviews' }
      ]
    },
    {
      id: 'support',
      label: 'Legal & Support',
      icon: AlertCircle,
      color: 'from-red-500 to-red-600',
      subcategories: [
        { id: 'Legal & Safety', label: 'Legal & Safety' },
        { id: 'Privacy & Security', label: 'Privacy & Security' },
        { id: 'Troubleshooting', label: 'Troubleshooting' }
      ]
    }
  ];

  const handleCategoryClick = (categoryId) => {
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(categoryId);
    }
  };

  const handleSubcategoryClick = (subcategoryId) => {
    onCategoryChange(subcategoryId);
    setExpandedCategory(null);
  };

  return (
    <div className="w-full">
      {/* Main Category Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {categories.map((category, idx) => {
          const Icon = category.icon;
          const isSelected = selectedCategory && category.subcategories.some(s => s.id === selectedCategory);
          const isExpanded = expandedCategory === category.id;

          return (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover="hover"
              transition={{ duration: 0.3, delay: idx * 0.05 }}
              viewport={{ once: true }}
              className="relative group"
            >

              <div
                className={`w-full h-full relative overflow-hidden rounded-xl p-6 transition-all duration-500 border ${isSelected || isExpanded
                  ? 'bg-gradient-to-br from-primary/20 via-primary/5 to-transparent border-primary/50 shadow-[0_0_30px_-5px_rgba(16,185,129,0.3)]'
                  : 'bg-card/20 border-white/5 group-hover:border-green-500/50 group-hover:bg-green-500/10 group-hover:shadow-[0_0_20px_-5px_rgba(16,185,129,0.2)]'
                  }`}
              >
                {/* Active Indicator Line */}
                {(isSelected || isExpanded) && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-80" />
                )}

                {/* Icon */}
                <div className={`relative z-10 flex flex-col items-center justify-center h-full`}>
                  <motion.div
                    variants={{ hover: { scale: 1.2, rotate: 5 } }}
                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                    className={`p-3 rounded-full mb-3 transition-colors duration-300 ${isSelected || isExpanded
                      ? 'bg-primary/20 text-primary ring-1 ring-primary/50 shadow-[0_0_15px_rgba(16,185,129,0.3)]'
                      : 'bg-white/5 text-muted-foreground group-hover:text-primary group-hover:bg-primary/10'
                      }`}>
                    <Icon className="w-6 h-6" />
                  </motion.div>

                  <p className={`text-sm font-bold text-center leading-tight transition-all duration-300 ${isSelected || isExpanded ? 'text-white' : 'text-muted-foreground group-hover:text-white'
                    }`}>
                    {category.label}
                  </p>

                  <ChevronDown
                    className={`w-4 h-4 mt-2 transition-transform duration-300 ${isExpanded ? 'rotate-180 text-primary' : 'text-transparent group-hover:text-muted-foreground/50'
                      }`}
                  />
                </div>
              </div>

              <button
                onClick={() => handleCategoryClick(category.id)}
                className="absolute inset-0 w-full h-full z-20 cursor-pointer"
                aria-label={`Select ${category.label}`}
              />

              {/* Subcategories Dropdown */}
              {isExpanded && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 right-0 mt-2 z-50 bg-card border border-white/10 rounded-lg shadow-xl overflow-hidden"
                >
                  {category.subcategories.map((subcategory) => (
                    <motion.button
                      key={subcategory.id}
                      onClick={() => handleSubcategoryClick(subcategory.id)}
                      className={`w-full px-4 py-3 text-left text-sm transition-all hover:bg-primary/10 border-b border-white/5 last:border-b-0 ${selectedCategory === subcategory.id
                        ? 'bg-primary/20 text-primary font-semibold'
                        : 'text-foreground hover:text-primary'
                        }`}
                      whileHover={{ x: 4 }}
                    >
                      {subcategory.label}
                    </motion.button>
                  ))}
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>


    </div>
  );
};
